import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { goTop } from "@/lib/embedded";
import { sectionPath } from "@/components/job/Lifecycle";
import { AskTeammateDialog } from "@/components/assessment/AskTeammates";
import { approveTrial } from "@/components/assessment/approve";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { countItems, totalMinutes } from "@/components/assessment/model";

function buildSteps(test, gate, lc, live) {
  const rubric = test.rubric || [];
  const weightOk = rubric.length > 0 && rubric.reduce((a, r) => a + (Number(r.weight) || 0), 0) === 100;
  const qr = test.quality_report;
  const n = qr?.checks?.length || 0;
  const hasTrial = !!gate?.has_trial;
  const approved = !!gate?.approved;
  const minutes = totalMinutes(test);
  const waiting = lc?.signoff_waiting || [];
  return [
    { key: "blueprint", title: "Blueprint matches role", sub: `${rubric.length} competencies, weights total ${weightOk ? "100%" : "≠ 100%"}`, done: weightOk },
    { key: "validated", title: "Questions validated", sub: qr ? `${n} of ${n} automatic checks passed` : `${countItems(test)} questions, reviewed by hand`, done: true },
    { key: "trial", title: "Trial run", sub: hasTrial ? "Done. Run it again after any change" : `Take it yourself${minutes ? `, about ${minutes} min` : ""}`, done: hasTrial, action: "trial", label: "Start trial run" },
    { key: "signoff", title: "Approval", done: approved, action: hasTrial && !approved ? "approve" : null, label: live ? "Approve" : "Approve and publish",
      sub: approved ? `Approved by ${gate.approval?.reviewer || "your team"}` : !hasTrial ? "Approve the trial report to publish" : waiting.length ? `Waiting on ${waiting.join(", ")}, or approve it yourself` : "Approve it yourself, or ask a teammate to review" },
  ];
}

function Dot({ state, n }) {
  return (
    <span className={cn("h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0",
      state === "done" ? "bg-primary/15 text-primary" : state === "current" ? "border-2 border-amber-400 text-amber-600 dark:text-amber-300" : "border border-foreground/20 text-muted-foreground")}>
      {state === "done" ? <Check className="h-3 w-3" /> : n}
    </span>
  );
}

function StepAction({ s, run, busy }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
      <Button size="sm" onClick={run[s.action]} disabled={busy[s.action]} data-testid={`checklist-${s.action}`} className="gap-1.5">
        {busy[s.action] && <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" />}{s.label}<ArrowRight className="h-3.5 w-3.5" />
      </Button>
      {s.action === "approve" && <>
        <button onClick={run.report} className="text-xs text-primary hover:underline" data-testid="checklist-report">Open trial report</button>
        <button onClick={run.ask} className="text-xs text-muted-foreground hover:text-foreground" data-testid="checklist-ask">Ask a teammate</button>
      </>}
    </span>
  );
}

// The whole path to publish in one slim bar pinned above the questions, with the next action always in view.
export function ReadinessChecklist({ jobId, test, gate, lifecycle, live, title, onTrial, trialing, onChanged }) {
  const nav = useNavigate();
  const [asking, setAsking] = useState(false);
  const [approving, setApproving] = useState(false);
  const steps = buildSteps(test, gate, lifecycle, live);
  const current = steps.findIndex((s) => !s.done);
  const cur = steps[current];
  const approve = async () => {
    setApproving(true);
    const res = await approveTrial(gate.latest_trial_id, { publish: !live, title });
    setApproving(false);
    if (res) onChanged?.();
  };
  const run = { trial: onTrial, approve, ask: () => setAsking(true), report: () => goTop(nav, `/recruiter/trial-report/${gate.latest_trial_id}`) };
  const busy = { trial: trialing, approve: approving };
  const cont = gate?.approved && <Button size="sm" variant={live ? "outline" : "default"} className="gap-1.5" onClick={() => goTop(nav, sectionPath(jobId, "publish"))} data-testid="checklist-continue">{live ? "See it live" : "Continue to publish"}<ArrowRight className="h-4 w-4" /></Button>;
  const rerun = gate?.has_trial && cur?.key !== "trial";
  return (
    <section className="ready-bar hs-card px-4 py-2.5 space-y-1 shrink-0" data-testid="readiness-checklist" data-layout="bar">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <h2 className="text-sm font-semibold shrink-0">Ready to publish?</h2>
        <ol className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5">
          {steps.map((s, i) => {
            const state = s.done ? "done" : i === current ? "current" : "todo";
            return (
              <li key={s.key} title={`${s.title}: ${s.sub}`} aria-label={`${s.title}, ${state === "done" ? "done" : state === "current" ? "next" : "to do"}`}
                className="ready-step flex items-center gap-1.5 text-xs" data-testid={`ready-${s.key}`} data-state={state}>
                <Dot state={state} n={i + 1} />
                <span className={cn("ready-step-label whitespace-nowrap", state === "current" ? "font-semibold text-foreground" : "text-muted-foreground")}>{s.title}</span>
              </li>
            );
          })}
        </ol>
        <div className="ml-auto">{cont || (cur?.action && <StepAction s={cur} run={run} busy={busy} />)}</div>
      </div>
      <p className="text-xs text-muted-foreground leading-5" data-testid="checklist-gate-note">
        {(cur || steps[steps.length - 1]).sub}.
        {rerun && <> <button onClick={onTrial} disabled={trialing} className="text-primary hover:underline disabled:opacity-50" data-testid="checklist-rerun">Run the trial again</button></>}
      </p>
      <AskTeammateDialog trialId={gate?.latest_trial_id} open={asking} onOpenChange={setAsking} onSent={onChanged} />
    </section>
  );
}
